/**
 * Il vocabolario del CRM, in un posto solo: come si chiama uno stato, di che
 * colore è la sua pillola, come si scrive una data. Clienti, Calendario e
 * Messaggi leggono tutti da qui, così «Nuovo appuntamento» non diventa «Nuovo app.»
 * su una schermata e «In attesa» su un'altra.
 */

import { messaggioErrore } from "@/lib/preventivi";
import { eur } from "@/lib/formato";

/* Lo stato commerciale (vista `clienti_stato`, D5 del piano). Il colore segue
   il significato che ha in tutta l'app: attenzione = serve un gesto, errore =
   qualcosa è andato storto, info = in corso, ok = chiuso bene.
   «da_confermare» è «Nuovo appuntamento»: una visita in agenda, o da fissare (0041). */
export const STATO_COMMERCIALE = {
  da_confermare: { label: "Nuovo appuntamento", pill: "attenzione", punto: "bg-chart-2" },
  appuntamento_fatto: { label: "Appuntamento fatto", pill: "neutro", punto: "bg-brand/70" },
  no_show: { label: "No show", pill: "errore", punto: "bg-danger" },
  preventivo_inviato: { label: "Preventivo inviato", pill: "info", punto: "bg-brand" },
  in_trattativa: { label: "In trattativa", pill: "info", punto: "bg-warning" },
  chiuso: { label: "Chiuso", pill: "ok", punto: "bg-success" },
  perso: { label: "Perso", pill: "neutro", punto: "bg-chart-neutral" },
};

/* Il pallino è decorazione (il nome dello stato c'è sempre accanto): le classi sono scritte per intero
   qui sopra, così Tailwind le trova nel sorgente. */
export const statoCommerciale = (codice) => STATO_COMMERCIALE[codice] || STATO_COMMERCIALE.da_confermare;

/** Il valore di un cliente come lo legge lo showroom: «~ 22.000 €» se è una stima, pieno se firmato. */
export function valoreBreve(r) {
  if (r.valore === null || r.valore === undefined) return "—";
  return `${r.valore_nota === "stimato" ? "~ " : ""}${eur(r.valore, { decimali: 0 })}`;
}

/* Lo stato di un appuntamento (i cinque della D2). */
export const STATO_APPUNTAMENTO = {
  programmato: { label: "In agenda", pill: "attenzione" },
  riprogrammato: { label: "Spostato", pill: "attenzione" },
  presentato: { label: "Presentato", pill: "ok" },
  no_show: { label: "No show", pill: "errore" },
  annullato: { label: "Annullato", pill: "neutro" },
};

/* Una visita in showroom in agenda aspetta l'esito (pillola d'attenzione); un rilievo o
   una consegna sono semplicemente in agenda. La conferma la manda WhatsApp: nessuno chiama. */
export function statoAppuntamento(stato, tipo) {
  if (tipo && tipo !== "visita_showroom") {
    if (stato === "programmato" || stato === "riprogrammato") return { label: "In agenda", pill: "info" };
    if (stato === "presentato") return { label: "Fatto", pill: "ok" };
    if (stato === "no_show") return { label: "Non fatto", pill: "errore" };
  }
  return STATO_APPUNTAMENTO[stato] || STATO_APPUNTAMENTO.programmato;
}

/** Una visita in showroom passata e ancora senza esito: «Da segnare», nel Calendario e nella scheda. */
export const daSegnare = (a) =>
  a.tipo === "visita_showroom" && ["programmato", "riprogrammato"].includes(a.stato) && passato(a.data_inizio);

export const TIPO_APPUNTAMENTO = {
  visita_showroom: "Showroom",
  rilievo: "Rilievo misure",
  consegna: "Consegna e montaggio",
  montaggio: "Montaggio",
};

/* Le fonti che si scelgono a mano, anche più d'una. L'ORDINE È LA PRIORITÀ: la prima
   scelta in quest'ordine è la fonte che conta nel cruscotto, le altre si aggiungono
   (D12). Prima l'origine, poi il canale con cui ha scritto o è entrato. «Non tracciata»
   e «Landing» non si offrono: le scrive la porta delle prenotazioni. */
export const FONTI_A_MANO = [
  { codice: "campagna_meta", label: "Campagna Meta" },
  { codice: "passaparola", label: "Passaparola" },
  { codice: "instagram", label: "Instagram" },
  { codice: "google", label: "Google" },
  { codice: "insegna", label: "Insegna" },
  { codice: "whatsapp", label: "WhatsApp" },
  { codice: "showroom", label: "Si è presentato in showroom" },
  { codice: "altro", label: "Altro" },
];

/* Le date si leggono sull'ora dello showroom, non su quella del computer. */
const TZ = "Europe/Rome";
const fmtGiorno = new Intl.DateTimeFormat("it-IT", { weekday: "short", day: "numeric", month: "short", timeZone: TZ });
const fmtGiornoAnno = new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "short", year: "numeric", timeZone: TZ });
const fmtOra = new Intl.DateTimeFormat("it-IT", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: TZ });

function comeData(valore) {
  if (!valore) return null;
  /* Una data senza ora («2026-10-02», da una colonna `date`) si legge come
     mezzogiorno: letta come mezzanotte UTC, in certi fusi diventerebbe il
     giorno prima. */
  const d = /^\d{4}-\d{2}-\d{2}$/.test(valore) ? new Date(`${valore}T12:00:00Z`) : new Date(valore);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** «gio 2 ott» — dentro l'anno in corso l'anno non si scrive. */
export function giorno(valore) {
  const d = comeData(valore);
  if (!d) return "—";
  return d.getFullYear() === new Date().getFullYear() ? fmtGiorno.format(d) : fmtGiornoAnno.format(d);
}

/** «gio 2 ott, 15:00» */
export function giornoOra(valore) {
  const d = comeData(valore);
  if (!d) return "—";
  return `${giorno(valore)}, ${fmtOra.format(d)}`;
}

export function ora(valore) {
  const d = comeData(valore);
  return d ? fmtOra.format(d) : "—";
}

/** «oggi 11:42» · «ieri 18:05» · «gio 2 ott, 15:00»: quando è arrivato un lead. */
export function arrivo(valore) {
  const g = giornoDi(valore);
  if (!g) return "—";
  if (g === oggi()) return `oggi ${ora(valore)}`;
  if (g === piuGiorni(oggi(), -1)) return `ieri ${ora(valore)}`;
  return giornoOra(valore);
}

/** «28 ott» senza il giorno della settimana: le date dentro una riga stretta. */
const fmtCorto = new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "short", timeZone: TZ });
export function giornoCorto(valore) {
  const d = comeData(valore);
  return d ? fmtCorto.format(d) : "—";
}

/** «oggi alle 16:00» · «domani alle 10:00» · «gio 29, 10:00»: quando, detto come lo dice lo showroom. */
export function quandoParlato(valore) {
  const g = giornoDi(valore);
  if (!g) return "—";
  if (g === oggi()) return `oggi alle ${ora(valore)}`;
  if (g === piuGiorni(oggi(), 1)) return `domani alle ${ora(valore)}`;
  if (g === piuGiorni(oggi(), -1)) return `ieri alle ${ora(valore)}`;
  return giornoOra(valore);
}

/** Vero se l'istante è già passato. */
export function passato(valore) {
  const d = comeData(valore);
  return d ? d.getTime() < Date.now() : false;
}

/** Il messaggio d'errore del backend, o quello di rete, sempre come FRASE: un 422 di
 *  validazione ha `detail` = elenco di oggetti, e passato così a un toast faceva cadere la pagina. */
export function erroreDi(e) {
  return messaggioErrore(e, "Qualcosa non ha funzionato");
}

/* ── I giorni del calendario ─────────────────────────────────────────────
   Un giorno è una stringa «2026-10-02» sull'ora di Roma. I conti si fanno a
   mezzogiorno UTC, così il cambio dell'ora legale non sposta mai un giorno. */
const fmtChiave = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: TZ });

/** Il giorno di Roma di un istante: «2026-10-02». */
export function giornoDi(valore) {
  const d = comeData(valore);
  return d ? fmtChiave.format(d) : null;
}

export function oggi() {
  return fmtChiave.format(new Date());
}

function aMezzogiorno(chiave) {
  const [a, m, g] = chiave.split("-").map(Number);
  return new Date(Date.UTC(a, m - 1, g, 12));
}

export function piuGiorni(chiave, n) {
  const d = aMezzogiorno(chiave);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Lo stesso giorno fra `n` mesi; se quel mese è più corto, l'ultimo giorno del mese («31 ott» + 4 → «28 feb»). */
export function piuMesi(chiave, n) {
  const [a, m, g] = chiave.split("-").map(Number);
  const ultimo = new Date(Date.UTC(a, m - 1 + n + 1, 0)).getUTCDate();
  return new Date(Date.UTC(a, m - 1 + n, Math.min(g, ultimo), 12)).toISOString().slice(0, 10);
}

/** Il lunedì della settimana di quel giorno. */
export function lunediDi(chiave) {
  const dow = aMezzogiorno(chiave).getUTCDay(); // 0 = domenica
  return piuGiorni(chiave, dow === 0 ? -6 : 1 - dow);
}

const fmtTesta = new Intl.DateTimeFormat("it-IT", { weekday: "short", day: "numeric", timeZone: "UTC" });
const fmtLungo = new Intl.DateTimeFormat("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
const fmtMese = new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

/** «lun 22» */
export function testaGiorno(chiave) {
  return fmtTesta.format(aMezzogiorno(chiave));
}

/** «lunedì 22 settembre» */
export function giornoLungo(chiave) {
  return fmtLungo.format(aMezzogiorno(chiave));
}

/** «22 – 28 set 2026» */
export function intervallo(da, a) {
  if (da === a) return fmtMese.format(aMezzogiorno(da));
  const [d1, d2] = [aMezzogiorno(da), aMezzogiorno(a)];
  const stessoMese = d1.getUTCMonth() === d2.getUTCMonth();
  return `${stessoMese ? d1.getUTCDate() : fmtMese.format(d1).replace(/ \d{4}$/, "")} – ${fmtMese.format(d2)}`;
}

/* ── La settimana di arrivo della merce (0046) ─────────────────────────────
   Come la scrive il fornitore, «settimana 47», con l'anno. ISO 8601: la settimana
   parte di lunedì e la 1 è quella del 4 gennaio. Il gestionale salva il lunedì. */
const MESI_LUNGHI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto",
  "settembre", "ottobre", "novembre", "dicembre"];

/** Il lunedì della settimana `n` dell'`anno`, «2026-11-16»; null se quell'anno non ce l'ha. */
export function lunediDellaSettimana(n, anno) {
  if (!Number.isInteger(n) || !Number.isInteger(anno) || n < 1 || n > 53) return null;
  const lunedi = piuGiorni(lunediDi(`${anno}-01-04`), (n - 1) * 7);
  // Una settimana è dell'anno in cui cade il suo giovedì: la 53 c'è solo in certi anni.
  return piuGiorni(lunedi, 3).startsWith(String(anno)) ? lunedi : null;
}

/** «2026-11-16» → { numero: 47, anno: 2026 } */
export function settimanaDi(giorno) {
  const lunedi = lunediDi(giorno);
  const anno = Number(piuGiorni(lunedi, 3).slice(0, 4));
  const numero = Math.round((aMezzogiorno(lunedi) - aMezzogiorno(lunediDi(`${anno}-01-04`))) / 604800000) + 1;
  return { numero, anno };
}

/** «2026-11-16» → «16-22 novembre 2026»: il dialogo della conferma (la tappa scrive la forma corta, backend `crm.settimana_testo`). */
export function giorniDellaSettimana(lunedi) {
  const [a1, m1, g1] = lunedi.split("-").map(Number);
  const [a2, m2, g2] = piuGiorni(lunedi, 6).split("-").map(Number);
  if (a1 !== a2) return `${g1} ${MESI_LUNGHI[m1 - 1]} ${a1} - ${g2} ${MESI_LUNGHI[m2 - 1]} ${a2}`;
  if (m1 !== m2) return `${g1} ${MESI_LUNGHI[m1 - 1]} - ${g2} ${MESI_LUNGHI[m2 - 1]} ${a2}`;
  return `${g1}-${g2} ${MESI_LUNGHI[m1 - 1]} ${a1}`;
}

/** L'anno della «settimana n» scritta su una conferma arrivata il giorno `riferimento`:
    la prima settimana n che non è già finita. A dicembre, «settimana 3» è di gennaio. */
export function annoDellaSettimana(n, riferimento) {
  const anno = Number(riferimento.slice(0, 4));
  const lunedi = lunediDellaSettimana(n, anno);
  return lunedi && piuGiorni(lunedi, 6) >= riferimento ? anno : anno + 1;
}
